import { Card, CardContent, Typography } from '@mui/material';
import BasicSelect from 'further-ui/components/BasicSelect';
import { Column, Row } from 'further-ui/components/Layout';
import Switch from 'further-ui/components/Switch';
import { Controller } from 'react-hook-form';
import useReviewSettings from './useReviewSettings';

type Props = {
  vehicleId: string;
};

const ReviewSettings: React.FC<Props> = ({ vehicleId }) => {
  const {
    control,
    adminUsersOptions,
    handleChangeInvestorDetailsNeedsReview,
    handleChangeInvestorDetailsReviewerIds,
    handleChangeClassificationNeedsReview,
    handleChangeClassificationReviewerIds,
    handleChangeTaxDetailsNeedsReview,
    handleChangeTaxDetailsReviewerIds,
    isLoading,
  } = useReviewSettings(vehicleId);

  return (
    <Card variant="outlined">
      <CardContent>
        <Column spacing="lg" fullWidth>
          <Typography variant="h6">Onboarding review settings</Typography>
          <Typography variant="body2">
            Select which onboarding steps are subject to review even if
            completed correctly. These settings will override any firm settings
            where steps are completed as part of vehicle onboarding. KYC/KYB is
            always subject to review.
          </Typography>
          <Column spacing="xs" fullWidth>
            <Row centered fullWidth spacing="sm" justify="space-between">
              <Typography variant="h6">
                Investor details require review
              </Typography>
              <Controller
                control={control}
                name="reviewSettings.investorDetailsNeedsReview"
                render={({ field }) => (
                  <Switch
                    disabled={isLoading}
                    checked={field.value}
                    onChange={handleChangeInvestorDetailsNeedsReview}
                  />
                )}
              />
            </Row>
            <Controller
              control={control}
              name="reviewSettings.investorDetailsReviewerIds"
              render={({ field }) => (
                <BasicSelect
                  disabled={isLoading}
                  fullWidth
                  size="small"
                  value={field.value}
                  onChange={(values) => {
                    handleChangeInvestorDetailsReviewerIds(values);
                  }}
                  options={adminUsersOptions}
                  multiple
                  placeholder="Select reviewer(s)"
                />
              )}
            />
          </Column>

          <Column spacing="xs" fullWidth>
            <Row centered fullWidth spacing="sm" justify="space-between">
              <Typography variant="h6">
                Classifications require review
              </Typography>
              <Controller
                control={control}
                name="reviewSettings.classificationNeedsReview"
                render={({ field }) => (
                  <Switch
                    disabled={isLoading}
                    checked={field.value}
                    onChange={handleChangeClassificationNeedsReview}
                  />
                )}
              />
            </Row>
            <Controller
              control={control}
              name="reviewSettings.classificationReviewerIds"
              render={({ field }) => (
                <BasicSelect
                  disabled={isLoading}
                  fullWidth
                  size="small"
                  value={field.value}
                  onChange={(values: Array<string>) => {
                    handleChangeClassificationReviewerIds(values);
                  }}
                  options={adminUsersOptions}
                  multiple
                  placeholder="Select reviewer(s)"
                />
              )}
            />
          </Column>

          <Column spacing="xs" fullWidth>
            <Row centered fullWidth spacing="sm" justify="space-between">
              <Typography variant="h6">
                Tax/FATCA/CRS details require review
              </Typography>
              <Controller
                control={control}
                name="reviewSettings.taxDetailsNeedsReview"
                render={({ field }) => (
                  <Switch
                    disabled={isLoading}
                    checked={field.value}
                    onChange={handleChangeTaxDetailsNeedsReview}
                  />
                )}
              />
            </Row>
            <Controller
              control={control}
              name="reviewSettings.taxDetailsReviewerIds"
              render={({ field }) => (
                <BasicSelect
                  disabled={isLoading}
                  fullWidth
                  size="small"
                  value={field.value}
                  onChange={(values) => {
                    handleChangeTaxDetailsReviewerIds(values);
                  }}
                  options={adminUsersOptions}
                  multiple
                  placeholder="Select reviewer(s)"
                />
              )}
            />
          </Column>
        </Column>
      </CardContent>
    </Card>
  );
};

export default ReviewSettings;
